import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "../context";

/* Constellation Lines - galaxy night */
const ConstellationLines = () => {
  const constellations = useMemo(() => [
    { points: [[10,15],[15,10],[22,12],[20,20],[14,22]], color: "rgba(139,92,246,0.2)" },
    { points: [[60,8],[65,15],[72,12],[75,20],[68,25],[62,18]], color: "rgba(236,72,153,0.15)" },
    { points: [[35,50],[40,45],[48,48],[45,55],[38,58]], color: "rgba(34,211,238,0.18)" },
    { points: [[80,40],[85,35],[90,38],[88,45],[82,48]], color: "rgba(251,191,36,0.15)" },
  ], []);
  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none z-[6]" style={{ animation: "constellation-fade 4s ease-in forwards" }}>
      {constellations.map((c, ci) => (
        <g key={ci}>
          {c.points.map((p, pi) => pi < c.points.length - 1 ? (
            <line key={pi} x1={`${c.points[pi][0]}%`} y1={`${c.points[pi][1]}%`}
              x2={`${c.points[pi+1][0]}%`} y2={`${c.points[pi+1][1]}%`}
              stroke={c.color} strokeWidth="1" strokeDasharray="4 4">
              <animate attributeName="stroke-dashoffset" values="8;0" dur="3s" repeatCount="indefinite" />
            </line>
          ) : null)}
          {c.points.map((p, pi) => (
            <circle key={`dot-${pi}`} cx={`${p[0]}%`} cy={`${p[1]}%`} r="2" fill="white" opacity="0.5">
              <animate attributeName="opacity" values="0.3;0.7;0.3" dur={`${2 + pi * 0.5}s`} repeatCount="indefinite" />
            </circle>
          ))}
        </g>
      ))}
    </svg>
  );
};

/* Bioluminescence - ocean night */
const BioluminescenceOverlay = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-[6]">
    {Array.from({ length: 20 }, (_, i) => {
      const colors = ['rgba(34,211,238,0.3)', 'rgba(167,139,250,0.25)', 'rgba(52,211,153,0.3)', 'rgba(236,72,153,0.2)'];
      return (
        <div key={i} className="absolute rounded-full" style={{
          width: 4 + Math.random() * 8, height: 4 + Math.random() * 8,
          top: `${20 + Math.random() * 60}%`, left: `${Math.random() * 100}%`,
          background: colors[i % colors.length],
          boxShadow: `0 0 12px ${colors[i % colors.length]}`,
          animation: `biolum-pulse ${2 + Math.random() * 4}s ease-in-out ${Math.random() * 5}s infinite`,
        }} />
      );
    })}
  </div>
);

/* Fairy Lanterns - forest night */
const FairyLanterns = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-[6]">
    {Array.from({ length: 12 }, (_, i) => {
      const colors = ['#FDE68A', '#A78BFA', '#34D399', '#F472B6', '#67E8F9', '#FB923C'];
      return (
        <div key={i} className="absolute" style={{
          top: `${15 + Math.random() * 60}%`, left: `${5 + Math.random() * 90}%`,
          animation: `lantern-sway ${5 + Math.random() * 5}s ease-in-out ${Math.random() * 6}s infinite`,
        }}>
          <div className="w-3 h-4 rounded-t-full" style={{
            background: `radial-gradient(ellipse, ${colors[i % colors.length]}, ${colors[i % colors.length]}80, transparent)`,
            boxShadow: `0 0 15px ${colors[i % colors.length]}60`,
          }} />
          <div className="w-px h-8 mx-auto" style={{ background: `${colors[i % colors.length]}20` }} />
        </div>
      );
    })}
  </div>
);

/* Northern Lights Max - aurora night */
const NorthernLightsMax = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-[6]">
    <div className="absolute top-0 left-0 w-full h-[70%]">
      {[0, 1, 2, 3].map(i => (
        <div key={i} className="absolute w-full h-full" style={{
          background: [
            "linear-gradient(180deg, rgba(34,211,238,0.25) 0%, rgba(52,211,153,0.18) 30%, transparent 70%)",
            "linear-gradient(180deg, rgba(167,139,250,0.2) 0%, rgba(236,72,153,0.15) 25%, transparent 60%)",
            "linear-gradient(180deg, rgba(52,211,153,0.18) 0%, rgba(34,211,238,0.2) 35%, transparent 65%)",
            "linear-gradient(180deg, rgba(59,130,246,0.15) 0%, rgba(167,139,250,0.18) 40%, transparent 75%)",
          ][i],
          animation: `aurora-wave ${10 + i * 4}s ease-in-out ${i * -3}s infinite`,
        }} />
      ))}
    </div>
  </div>
);

/* Grand Finale - fireworks night */
const GrandFinale = () => {
  const bursts = useMemo(() => Array.from({ length: 12 }, (_, i) => ({
    x: 5 + Math.random() * 90, y: 5 + Math.random() * 45,
    color: ["#EF4444", "#FBBF24", "#3B82F6", "#EC4899", "#22C55E", "#A78BFA", "#FB923C", "#22D3EE", "#F97316", "#34D399", "#8B5CF6", "#F472B6"][i],
    delay: i * 1.2, size: 80 + Math.random() * 100,
  })), []);
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-[6]">
      {bursts.map((b, i) => (
        <div key={i} className="absolute" style={{ left: `${b.x}%`, top: `${b.y}%` }}>
          <div className="firework-burst" style={{
            width: b.size, height: b.size,
            background: `radial-gradient(circle, ${b.color}80, ${b.color}20, transparent 65%)`,
            animationDelay: `${b.delay}s`,
            boxShadow: `0 0 ${b.size / 2}px ${b.color}40`,
          }} />
          {Array.from({ length: 12 }, (_, j) => (
            <div key={j} className="firework-spark" style={{
              background: b.color, boxShadow: `0 0 6px ${b.color}`,
              animationDelay: `${b.delay + 0.1}s`,
              '--angle': `${(j / 12) * 360}deg`, '--dist': `${b.size * 0.6}px`,
            }} />
          ))}
        </div>
      ))}
    </div>
  );
};

/* Thunder Flash - storm night */
const ThunderFlash = () => (
  <div className="absolute inset-0 pointer-events-none z-[6]">
    <div className="lightning-flash-intense" />
  </div>
);

/* Hieroglyph Glow - egypt night */
const HieroglyphGlow = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-[6]">
    {Array.from({ length: 15 }, (_, i) => {
      const glyphs = ['\u2625', '\u2641', '\u2600', '\u263D', '\u2605', '\u25C6', '\u25B2', '\u2726', '\u2B50', '\u2B22'];
      return (
        <div key={i} className="absolute" style={{
          fontSize: `${12 + Math.random() * 16}px`,
          top: `${Math.random() * 80}%`, left: `${Math.random() * 100}%`,
          color: 'rgba(251,191,36,0.25)',
          textShadow: '0 0 10px rgba(251,191,36,0.3), 0 0 20px rgba(251,191,36,0.2)',
          animation: `hieroglyph-float ${6 + Math.random() * 6}s ease-in-out ${Math.random() * 8}s infinite`,
        }}>{glyphs[i % glyphs.length]}</div>
      );
    })}
  </div>
);

/* Lava Rivers - volcano night */
const LavaRivers = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-[6]">
    <svg className="absolute bottom-0 left-0 w-full h-[35%]" preserveAspectRatio="none" viewBox="0 0 1440 300">
      {[0, 1, 2].map(i => (
        <path key={i} fill="none" strokeWidth="3" stroke={['rgba(239,68,68,0.3)', 'rgba(251,146,60,0.25)', 'rgba(234,179,8,0.2)'][i]}>
          <animate attributeName="d" dur={`${6 + i * 2}s`} repeatCount="indefinite"
            values={`M${100 + i * 400},0 Q${200 + i * 400},100 ${150 + i * 400},200 Q${100 + i * 400},300 ${180 + i * 400},300;M${100 + i * 400},0 Q${150 + i * 400},80 ${200 + i * 400},200 Q${250 + i * 400},300 ${130 + i * 400},300;M${100 + i * 400},0 Q${200 + i * 400},100 ${150 + i * 400},200 Q${100 + i * 400},300 ${180 + i * 400},300`} />
        </path>
      ))}
    </svg>
    <div className="absolute bottom-0 left-0 w-full h-[15%]" style={{
      background: "linear-gradient(0deg, rgba(239,68,68,0.2), rgba(251,146,60,0.1), transparent)",
      animation: "lava-glow-pulse 3s ease-in-out infinite",
    }} />
  </div>
);

/* Bioluminescent Waves - beach night */
const BioluminescentWaves = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-[6]">
    <svg className="absolute bottom-0 left-0 w-full h-[20%]" preserveAspectRatio="none" viewBox="0 0 1440 200">
      <path fill="none" strokeWidth="2" stroke="rgba(34,211,238,0.3)">
        <animate attributeName="d" dur="5s" repeatCount="indefinite"
          values="M0,100 Q240,70 480,100 Q720,130 960,100 Q1200,70 1440,100;M0,110 Q240,80 480,110 Q720,140 960,110 Q1200,80 1440,110;M0,100 Q240,70 480,100 Q720,130 960,100 Q1200,70 1440,100" />
      </path>
      <path fill="none" strokeWidth="2" stroke="rgba(52,211,153,0.2)">
        <animate attributeName="d" dur="7s" repeatCount="indefinite"
          values="M0,130 Q240,100 480,130 Q720,160 960,130 Q1200,100 1440,130;M0,120 Q240,90 480,120 Q720,150 960,120 Q1200,90 1440,120;M0,130 Q240,100 480,130 Q720,160 960,130 Q1200,100 1440,130" />
      </path>
    </svg>
    {Array.from({ length: 10 }, (_, i) => (
      <div key={i} className="absolute rounded-full" style={{
        width: 3, height: 3, bottom: `${2 + Math.random() * 15}%`, left: `${Math.random() * 100}%`,
        background: ['rgba(34,211,238,0.4)', 'rgba(52,211,153,0.35)'][i % 2],
        boxShadow: `0 0 8px ${['rgba(34,211,238,0.3)', 'rgba(52,211,153,0.25)'][i % 2]}`,
        animation: `biolum-pulse ${2 + Math.random() * 3}s ease-in-out ${Math.random() * 4}s infinite`,
      }} />
    ))}
  </div>
);

/* Glow Candy - candy night */
const GlowCandy = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-[6]">
    {Array.from({ length: 10 }, (_, i) => {
      const colors = ['#EC4899', '#A78BFA', '#FBBF24', '#34D399', '#FB923C'];
      return (
        <div key={i} className="absolute rounded-full" style={{
          width: 6 + Math.random() * 8, height: 6 + Math.random() * 8,
          top: `${10 + Math.random() * 70}%`, left: `${Math.random() * 100}%`,
          background: colors[i % colors.length],
          boxShadow: `0 0 20px ${colors[i % colors.length]}60`,
          opacity: 0.3,
          animation: `candy-float ${5 + Math.random() * 5}s ease-in-out ${Math.random() * 6}s infinite`,
        }} />
      );
    })}
  </div>
);

/* ====== DISPATCHER ====== */
const NighttimeEnhancements = ({ isNight }) => {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    if (!isNight) return;
    axios.get(`${API}/nighttime/config`).then(r => setConfig(r.data)).catch(() => {});
  }, [isNight]);

  if (!isNight || !config) return null;

  const special = config.config?.special_night;

  return (
    <>
      {special === "constellation-lines" && <ConstellationLines />}
      {special === "bioluminescence" && <BioluminescenceOverlay />}
      {special === "fairy-lanterns" && <FairyLanterns />}
      {special === "northern-lights-max" && <NorthernLightsMax />}
      {special === "grand-finale" && <GrandFinale />}
      {special === "thunder-flash" && <ThunderFlash />}
      {special === "pharaoh-stars" && <HieroglyphGlow />}
      {special === "hieroglyph-glow" && <HieroglyphGlow />}
      {special === "lava-rivers" && <LavaRivers />}
      {special === "bioluminescent-waves" && <BioluminescentWaves />}
      {special === "glow-candy" && <GlowCandy />}
    </>
  );
};

export default NighttimeEnhancements;
